import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { 
  MagnifyingGlassIcon,
  CommandLineIcon,
  UserIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  CalendarIcon,
  MapPinIcon,
  SparklesIcon,
  ArrowRightIcon,
  PlusIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { Button } from './button';
import { Card } from './card';
import { Badge } from './badge';

const CommandPalette = ({
  isOpen = false,
  onClose,
  onAction,
  className = ''
}) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentActions, setRecentActions] = useState([]);

  // Load recent actions from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('command-palette-recent');
    if (saved) {
      try {
        setRecentActions(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to parse recent actions:', error);
      }
    }
  }, []);

  // Mock data - in real app, this would come from API
  const actions = useMemo(() => [
    // Quick Actions
    {
      id: 'create-client',
      title: 'Create New Client',
      description: 'Add a new client to the CRM',
      icon: UserIcon,
      category: 'Quick Actions',
      keywords: ['client', 'new', 'add', 'create'],
      action: () => onAction?.('navigate', '/clients/new')
    },
    {
      id: 'create-task',
      title: 'Create Task',
      description: 'Add a new task or reminder',
      icon: ClipboardDocumentListIcon,
      category: 'Quick Actions',
      keywords: ['task', 'todo', 'reminder', 'create'],
      action: () => onAction?.('navigate', '/tasks/new')
    },
    {
      id: 'schedule-meeting',
      title: 'Schedule Meeting',
      description: 'Book a meeting with a client',
      icon: CalendarIcon,
      category: 'Quick Actions',
      keywords: ['meeting', 'schedule', 'calendar', 'book'],
      action: () => onAction?.('navigate', '/calendar/new')
    },
    {
      id: 'send-email',
      title: 'Send Email',
      description: 'Compose and send an email',
      icon: DocumentTextIcon,
      category: 'Quick Actions',
      keywords: ['email', 'send', 'compose', 'message'],
      action: () => onAction?.('navigate', '/email/compose')
    },

    // Navigation
    {
      id: 'go-dashboard',
      title: 'Go to Dashboard',
      description: 'View the main dashboard',
      icon: BuildingOfficeIcon,
      category: 'Navigation',
      keywords: ['dashboard', 'home', 'main', 'overview'],
      action: () => onAction?.('navigate', '/dashboard')
    },
    {
      id: 'go-clients',
      title: 'Go to Clients',
      description: 'View all clients',
      icon: UserIcon,
      category: 'Navigation',
      keywords: ['clients', 'customers', 'people'],
      action: () => onAction?.('navigate', '/clients')
    },
    {
      id: 'go-tasks',
      title: 'Go to Tasks',
      description: 'View all tasks',
      icon: ClipboardDocumentListIcon,
      category: 'Navigation',
      keywords: ['tasks', 'todos', 'work'],
      action: () => onAction?.('navigate', '/tasks')
    },
    {
      id: 'go-calendar',
      title: 'Go to Calendar',
      description: 'View calendar and events',
      icon: CalendarIcon,
      category: 'Navigation',
      keywords: ['calendar', 'events', 'schedule'],
      action: () => onAction?.('navigate', '/calendar')
    },
    {
      id: 'go-analytics',
      title: 'Go to Analytics',
      description: 'View analytics and reports',
      icon: BuildingOfficeIcon,
      category: 'Navigation',
      keywords: ['analytics', 'reports', 'stats', 'metrics'],
      action: () => onAction?.('navigate', '/analytics')
    },

    // AI Actions
    {
      id: 'ai-summarize',
      title: 'AI Summarize Client',
      description: 'Get AI summary of client activity',
      icon: SparklesIcon,
      category: 'AI Assistant',
      keywords: ['ai', 'summarize', 'summary', 'assistant'],
      action: () => onAction?.('ai', 'summarize-client')
    },
    {
      id: 'ai-suggest',
      title: 'AI Suggest Next Steps',
      description: 'Get AI suggestions for client',
      icon: SparklesIcon,
      category: 'AI Assistant',
      keywords: ['ai', 'suggest', 'recommend', 'next'],
      action: () => onAction?.('ai', 'suggest-next-steps')
    },
    {
      id: 'ai-translate',
      title: 'AI Translate Text',
      description: 'Translate text to Spanish/English',
      icon: SparklesIcon,
      category: 'AI Assistant',
      keywords: ['ai', 'translate', 'language', 'spanish'],
      action: () => onAction?.('ai', 'translate-text')
    },

    // Settings
    {
      id: 'settings',
      title: 'Open Settings',
      description: 'Access application settings',
      icon: BuildingOfficeIcon,
      category: 'Settings',
      keywords: ['settings', 'preferences', 'config'],
      action: () => onAction?.('navigate', '/settings')
    },
    {
      id: 'help',
      title: 'Open Help',
      description: 'Access help and documentation',
      icon: DocumentTextIcon,
      category: 'Settings',
      keywords: ['help', 'docs', 'documentation', 'support'],
      action: () => onAction?.('navigate', '/help')
    }
  ], [onAction]);

  const filteredActions = useMemo(() => {
    if (!query.trim()) {
      // Show recent actions first, then all actions
      const recentIds = recentActions.map(action => action.id);
      const recent = actions.filter(action => recentIds.includes(action.id));
      const others = actions.filter(action => !recentIds.includes(action.id));
      return [...recent, ...others];
    }

    const searchTerms = query.toLowerCase().split(' ');
    return actions.filter(action => {
      const searchableText = [
        action.title,
        action.description,
        action.category,
        ...action.keywords
      ].join(' ').toLowerCase();

      return searchTerms.every(term => searchableText.includes(term));
    });
  }, [query, actions, recentActions]);

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredActions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredActions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredActions[selectedIndex]) {
          handleActionSelect(filteredActions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose?.();
        break;
    }
  }, [filteredActions, selectedIndex, onClose]);

  const handleActionSelect = useCallback((action) => {
    // Add to recent actions
    const newRecent = [action, ...recentActions.filter(a => a.id !== action.id)].slice(0, 5);
    setRecentActions(newRecent);
    localStorage.setItem('command-palette-recent', JSON.stringify(newRecent));

    // Execute action
    action.action?.();
    onClose?.();
  }, [recentActions, onClose]);

  const groupedActions = useMemo(() => {
    const groups = {};
    filteredActions.forEach(action => {
      if (!groups[action.category]) {
        groups[action.category] = [];
      }
      groups[action.category].push(action);
    });
    return groups;
  }, [filteredActions]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-start justify-center pt-16">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl mx-4 animate-in slide-in-from-top-5 duration-200">
        {/* Header */}
        <div className="flex items-center space-x-3 p-4 border-b border-border">
          <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Type a command or search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 text-lg font-medium bg-background text-foreground placeholder-muted-foreground focus:outline-none"
            autoFocus
          />
          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
            <kbd className="px-2 py-1 bg-muted rounded text-xs">↑↓</kbd>
            <span>navigate</span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs">↵</kbd>
            <span>select</span>
            <kbd className="px-2 py-1 bg-muted rounded text-xs">esc</kbd>
            <span>close</span>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto">
          {Object.entries(groupedActions).map(([category, categoryActions]) => (
            <div key={category} className="p-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 px-2">
                {category}
              </h3>
              <div className="space-y-1">
                {categoryActions.map((action, index) => {
                  const Icon = action.icon;
                  const isSelected = filteredActions.indexOf(action) === selectedIndex;
                  const isRecent = recentActions.some(recent => recent.id === action.id);

                  return (
                    <button
                      key={action.id}
                      onClick={() => handleActionSelect(action)}
                      className={cn(
                        "w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors",
                        isSelected ? "bg-blue-50 dark:bg-blue-950/10 border border-blue-200 dark:border-blue-800" : "hover:bg-muted"
                      )}
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-foreground truncate">
                            {action.title}
                          </h4>
                          {isRecent && (
                            <Badge variant="outline" className="text-xs">
                              Recent
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {action.description}
                        </p>
                      </div>

                      <ArrowRightIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredActions.length === 0 && (
            <div className="p-8 text-center">
              <CommandLineIcon className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <h3 className="text-lg font-medium text-foreground mb-2">No results found</h3>
              <p className="text-muted-foreground">
                Try searching for "client", "task", "calendar", or "ai"
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted rounded-b-xl">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center space-x-4">
              <span className="flex items-center space-x-1">
                <CommandLineIcon className="h-3 w-3" />
                <span>Press ⌘K to open</span>
              </span>
              <span className="flex items-center space-x-1">
                <ClockIcon className="h-3 w-3" />
                <span>{recentActions.length} recent</span>
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <SparklesIcon className="h-3 w-3" />
              <span>AI-powered search</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;